import {
  DEFAULT_TRAINING_SETUP,
  EWSS_GRADES,
  TRAINING_CATEGORY_KEYS,
  TRAINING_CONSECUTIVE_FAIL_OPTIONS,
  TRAINING_DEFAULT_NO_EWSS_GAME_COUNT,
  TRAINING_NO_EWSS_GAME_COUNT_OPTIONS,
  TRAINING_POTION_AID_FILTER_OPTIONS,
  TRAINING_PLAYED_COUNT_OPTIONS,
  TRAINING_SPEED_OPTIONS,
} from "../constants/entrainementSetup";
import {
  normalizePlayedCountRange,
  sanitizeGroupementModeFilters,
  sanitizeIllustrationFilter,
  sanitizePlayedCountBound,
} from "../constants/registerFilters";

export const TRAINING_SETUP_STORAGE_KEY = "vocabularium_training_setup";

const VALID_GRADES = new Set(EWSS_GRADES);
const VALID_SPEEDS = new Set(TRAINING_SPEED_OPTIONS);
const VALID_CONSECUTIVE_FAIL_COUNTS = new Set(TRAINING_CONSECUTIVE_FAIL_OPTIONS);
const VALID_POTION_AID_FILTERS = new Set(
  TRAINING_POTION_AID_FILTER_OPTIONS.map((option) => option.value)
);
const VALID_NO_EWSS_GAME_COUNTS = new Set(
  TRAINING_NO_EWSS_GAME_COUNT_OPTIONS.map((option) => option.value)
);

function sanitizeString(value) {
  return typeof value === "string" ? value : "";
}

function sanitizeBoolean(value) {
  return Boolean(value);
}

function sanitizeCategories(raw = {}) {
  const categories = { ...DEFAULT_TRAINING_SETUP.categories };
  TRAINING_CATEGORY_KEYS.forEach((key) => {
    if (typeof raw[key] === "boolean") {
      categories[key] = raw[key];
    }
  });
  return categories;
}

export function sanitizeTrainingSetup(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_TRAINING_SETUP, categories: { ...DEFAULT_TRAINING_SETUP.categories } };
  }

  const exactGrade = sanitizeString(raw.exactGrade);
  const belowGrade = sanitizeString(raw.belowGrade);
  const aboveGrade = sanitizeString(raw.aboveGrade);
  const neverPlayed = sanitizeBoolean(raw.neverPlayed);
  let withoutEwssScore = sanitizeBoolean(raw.withoutEwssScore);
  let noEwssGameCount = sanitizeString(raw.noEwssGameCount);
  const ewssFlagsActive = neverPlayed || withoutEwssScore;
  const playedRange = normalizePlayedCountRange(raw.playedCountMin, raw.playedCountMax);
  const categories = sanitizeCategories(raw.categories);
  // Rétrocompat : anciennes clés noyauFavoris / noyauPrioritaires → catégories indépendantes
  if (sanitizeBoolean(raw.noyauFavoris)) categories.favoris = true;
  if (sanitizeBoolean(raw.noyauPrioritaires)) categories.prioritaires = true;

  if (withoutEwssScore) {
    noEwssGameCount = VALID_NO_EWSS_GAME_COUNTS.has(noEwssGameCount)
      ? noEwssGameCount
      : TRAINING_DEFAULT_NO_EWSS_GAME_COUNT;
  } else if (VALID_NO_EWSS_GAME_COUNTS.has(noEwssGameCount)) {
    withoutEwssScore = true;
  } else {
    noEwssGameCount = "";
  }

  if (neverPlayed) {
    withoutEwssScore = false;
    noEwssGameCount = "";
  }

  return {
    exactGrade: ewssFlagsActive ? "" : exactGrade && VALID_GRADES.has(exactGrade) ? exactGrade : "",
    belowGrade: ewssFlagsActive ? "" : belowGrade && VALID_GRADES.has(belowGrade) ? belowGrade : "",
    aboveGrade: ewssFlagsActive ? "" : aboveGrade && VALID_GRADES.has(aboveGrade) ? aboveGrade : "",
    neverPlayed,
    withoutEwssScore,
    noEwssGameCount,
    lastAppearanceFailed: sanitizeBoolean(raw.lastAppearanceFailed),
    consecutiveFails: sanitizeBoolean(raw.consecutiveFails),
    consecutiveFailsCount: VALID_CONSECUTIVE_FAIL_COUNTS.has(Number(raw.consecutiveFailsCount))
      ? Number(raw.consecutiveFailsCount)
      : DEFAULT_TRAINING_SETUP.consecutiveFailsCount,
    categories,
    ...sanitizeGroupementModeFilters(raw),
    illustration: sanitizeIllustrationFilter(raw.illustration),
    livre: sanitizeString(raw.livre),
    auteur: sanitizeString(raw.auteur),
    captureYear: sanitizeString(raw.captureYear),
    potionsUsed: sanitizeBoolean(raw.potionsUsed),
    potionsUsedFilter: VALID_POTION_AID_FILTERS.has(raw.potionsUsedFilter)
      ? raw.potionsUsedFilter
      : DEFAULT_TRAINING_SETUP.potionsUsedFilter,
    playedCountMin: playedRange.min,
    playedCountMax: playedRange.max,
    speedSeconds: VALID_SPEEDS.has(raw.speedSeconds)
      ? raw.speedSeconds
      : DEFAULT_TRAINING_SETUP.speedSeconds,
    withRepetition: sanitizeBoolean(raw.withRepetition),
  };
}

export function readTrainingSetupPreference() {
  try {
    const raw = localStorage.getItem(TRAINING_SETUP_STORAGE_KEY);
    if (!raw) {
      return sanitizeTrainingSetup(DEFAULT_TRAINING_SETUP);
    }
    return sanitizeTrainingSetup(JSON.parse(raw));
  } catch {
    return sanitizeTrainingSetup(DEFAULT_TRAINING_SETUP);
  }
}

export function writeTrainingSetupPreference(setup) {
  const value = sanitizeTrainingSetup(setup);

  try {
    localStorage.setItem(TRAINING_SETUP_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore quota / privacy mode errors
  }

  return value;
}
